// AccessGroup + AccessRow — the design's grouped "current access" lists
// (my-access.jsx AccessGroup / .ma-row). Each group is a flush card of rows;
// each row shows the app breadcrumb, item name, a time-remaining bar, the
// days-left label, an Extend action (wired to useDecideExtension), and a
// Revoke action on grants a request stands behind.

import { Box, Flex, Tooltip, useToast } from "@chakra-ui/react";
import { isAfter } from "date-fns";
import { useNavigate } from "react-router-dom";
import { humanDt } from "modules/utils";
import { appDisplayName } from "modules/apps/tenants";
import AppLogo from "components/ds/AppLogo";
import Bar from "components/ds/Bar";
import BreadcrumbRow from "components/ds/BreadcrumbRow";
import UtilityButton from "components/ds/UtilityButton";
import {
  MF_ACCESS,
  type MFSelectableResourceAccess,
} from "state/members/access_v2/catalogs";
import { extendAction } from "./extendAction";
import { useRevokeAccessFlow } from "./useRevokeAccessFlow";
import {
  accessMetrics,
  remainingBar,
  remainingLabel,
  urgencyTextColor,
  type AccessMetrics,
} from "./detailLogic";

// Re-exported from ./detailLogic (single source of truth) for existing callers.
export { accessMetrics, type AccessMetrics };

const dayLabel = (m: AccessMetrics): string => {
  if (m.indefinite) return "Indefinite";
  // Shared with the grant drawer — includes the graduated final-hour ladder
  // ("less than an hour left" etc.) instead of flattening to "0h left".
  return remainingLabel(m.daysLeft);
};

export interface AccessRowIdentity {
  itemName: string;
  /** Breadcrumb segments ("catalog › app"), deduped, never repeating itemName. */
  crumbs: string[];
  logoUrl?: string;
}

// The logo + name + breadcrumb a row shows, shared by the active and expired
// lists. Design breadcrumb: catalog › app (the catalog name + the catalog-item
// / "app" name). We deliberately do NOT fall back to the underlying resource
// name — for things like Slack channels that's a sibling resource (e.g.
// "#random") and reads as garbage. No catalog data ⇒ no breadcrumb. The SR's
// own image is often a legacy Feather-icon sentinel ("FiBox") rather than a URL
// — ignore those so the logo falls back to clean initials instead of a broken
// image.
export const accessRowIdentity = (
  access: MFSelectableResourceAccess,
): AccessRowIdentity => {
  const sr = access.selectableResource;
  const baseName = sr?.name ?? "Access";
  const itemName = appDisplayName({ ...sr, name: baseName });
  const crumbs = Array.from(
    new Set(
      [access.catalogName, access.appName].filter((s): s is string =>
        Boolean(s),
      ),
    ),
  ).filter((s) => s !== baseName);
  const srLogo =
    sr?.imageUrl && !/^Fi[A-Z]/.test(sr.imageUrl) ? sr.imageUrl : undefined;
  return { itemName, crumbs, logoUrl: access.appImageUrl ?? srLogo };
};

const AccessRow = ({
  access,
  divider,
  onOpen,
}: {
  access: MFSelectableResourceAccess;
  divider: boolean;
  onOpen?: (access: MFSelectableResourceAccess) => void;
}) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { mutate: decide, isLoading } = MF_ACCESS.useDecideExtension(access.id);
  const m = accessMetrics(access);
  const bar = remainingBar(m);

  const { itemName, crumbs, logoUrl } = accessRowIdentity(access);
  const {
    revoking,
    askToRevoke,
    confirmModal: revokeConfirm,
  } = useRevokeAccessFlow(access.id, itemName);
  const canExtend = Boolean(access.canExtend);

  const notYet =
    access.checkExtensionAt &&
    !isAfter(new Date(), new Date(access.checkExtensionAt));
  const extendTip = !canExtend
    ? notYet
      ? `Can be extended after ${humanDt(new Date(access.checkExtensionAt!))}`
      : "Not currently eligible for extension"
    : null;

  // Resources that mandate a reason can't be one-click extended — send the
  // user to the dedicated extension screen where the reason can be collected
  // (the same screen the chat "Review & extend" links open). The decision lives
  // in the `extendAction` helper so it stays typed and unit-tested.
  const extend = () => {
    const action = extendAction(access);
    if (action.kind === "navigate") {
      navigate(action.path);
      return;
    }
    decide({ result: "extend", selectableResourceAccessId: access.id }).then(() =>
      toast({ status: "success", variant: "subtle", title: "Access extended" }),
    );
  };

  const clickable = Boolean(onOpen);

  return (
    <Flex
      align="center"
      gap="lg" // 16px
      px="20px"
      py="14px"
      borderTopWidth={divider ? "1px" : undefined}
      borderTopStyle={divider ? "solid" : undefined}
      borderTopColor={divider ? "neutral.200" : undefined}
      {...(clickable
        ? {
            role: "button",
            cursor: "pointer",
            transition: "background-color 100ms ease-out",
            _hover: { bg: "neutral.50" },
            _active: { bg: "neutral.100" },
            onClick: () => onOpen!(access),
          }
        : {})}
    >
      <AppLogo size="lg" name={itemName} logoUrl={logoUrl} />

      <Box flex="1" minW={0}>
        {crumbs.length > 0 && <BreadcrumbRow segments={crumbs} />}
        <Box
          fontSize="15px"
          fontWeight={600}
          color="neutral.950"
          lineHeight="1.3"
          isTruncated
        >
          {itemName}
        </Box>
      </Box>

      {/* time-remaining bar — hidden on narrow viewports */}
      <Box w="200px" display={{ base: "none", lg: "block" }} flexShrink={0}>
        <Bar value={bar.pct} tone={bar.tone} />
      </Box>

      <Box
        w="90px"
        flexShrink={0}
        textAlign={{ base: "right", lg: "left" }}
        fontSize="smd" // 13px
        fontWeight={500}
        color={urgencyTextColor(bar.tone)}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {dayLabel(m)}
      </Box>

      {/* Inline quick action — stop the row click so it doesn't also open the
          drawer. */}
      <Flex flexShrink={0} gap="sm" onClick={(e) => e.stopPropagation()}>
        {access.canRevoke && (
          <UtilityButton
            size="md"
            treatment="subtle"
            sentiment="negative"
            isDisabled={revoking}
            onClick={askToRevoke}
          >
            Revoke
          </UtilityButton>
        )}
        <Tooltip label={extendTip} isDisabled={!extendTip} hasArrow>
          <Box>
            <UtilityButton
              size="md"
              treatment="dropshadow"
              sentiment="azurite"
              isDisabled={!canExtend || isLoading}
              onClick={extend}
            >
              Extend
            </UtilityButton>
          </Box>
        </Tooltip>
        {/* Inside the stopPropagation wrapper: the modal portals to <body>, but
            React still bubbles its backdrop click up to the row. */}
        {revokeConfirm}
      </Flex>
    </Flex>
  );
};

const AccessGroup = ({
  label,
  items,
  onOpen,
}: {
  label: string;
  items: MFSelectableResourceAccess[];
  onOpen?: (access: MFSelectableResourceAccess) => void;
}) => {
  if (!items.length) return null;
  return (
    <Box mb="1xl">
      <Box
        fontFamily="body"
        fontSize="md"
        fontWeight={600}
        color="neutral.900"
        mb="md"
      >
        {label}
      </Box>
      <Box
        bg="neutral.25"
        borderRadius="component.2xl" // 12px
        boxShadow="elevLow"
        overflow="hidden"
      >
        {items.map((a, i) => (
          <AccessRow key={a.id} access={a} divider={i > 0} onOpen={onOpen} />
        ))}
      </Box>
    </Box>
  );
};

export default AccessGroup;
